import { apiFetch } from "./api";
import type {
  AvatarUploadUrlRequest,
  AvatarUploadUrlResponse,
  FinalizeAvatarRequest,
  MeResponse,
} from "@family/shared";

const ALLOWED_MIMES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_BYTES = 2 * 1024 * 1024;

/** Max dimension we re-encode to client-side. Avatars are tiny on screen,
 *  so 1024 is plenty and keeps payloads small. */
const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.88;

/**
 * Compress + downscale an avatar file before upload. Returns the
 * original file when it's already small enough or compression fails
 * (e.g. unsupported format).
 *
 * Mobile cameras dump 6–10 MB JPEGs, which we can't accept under the
 * 2 MB cap. Re-encoding through canvas at 1024×1024 brings most
 * portraits well under 200 KB without visible quality loss at avatar
 * sizes.
 */
async function compressForAvatar(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
    return file;
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;

      // Skip work if both small and under the byte cap.
      if (w <= MAX_DIMENSION && h <= MAX_DIMENSION && file.size <= MAX_BYTES) {
        resolve(file);
        return;
      }

      const ratio = Math.min(MAX_DIMENSION / w, MAX_DIMENSION / h, 1);
      const newW = Math.round(w * ratio);
      const newH = Math.round(h * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size > MAX_BYTES) {
            resolve(file);
            return;
          }
          const compressed = new File([blob], "avatar.jpg", {
            type: "image/jpeg",
            lastModified: file.lastModified,
          });
          resolve(compressed);
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

/**
 * Two-step avatar upload, mirroring the family-logo and media flows:
 *   1. Compress client-side
 *   2. POST /me/avatar/upload-url → signed PUT URL + storagePath
 *   3. PUT bytes directly to GCS
 *   4. POST /me/avatar/finalize with storagePath + alsoUseOnTree
 *
 * Returns the new MeResponse so the caller can immediately update its
 * cache without an extra GET /me round-trip.
 */
export async function uploadAvatar(
  file: File,
  opts: { alsoUseOnTree?: boolean } = {},
): Promise<MeResponse> {
  const compressed = await compressForAvatar(file);

  if (!ALLOWED_MIMES.has(compressed.type)) {
    throw new Error("Avatar must be PNG, JPEG, or WebP.");
  }
  if (compressed.size > MAX_BYTES) {
    throw new Error("Avatar must be 2 MB or smaller.");
  }

  const reqBody: AvatarUploadUrlRequest = {
    mimeType: compressed.type as AvatarUploadUrlRequest["mimeType"],
    fileSize: compressed.size,
  };

  const signed = await apiFetch<AvatarUploadUrlResponse>(
    "/me/avatar/upload-url",
    {
      method: "POST",
      body: JSON.stringify(reqBody),
    },
  );

  const putRes = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": compressed.type },
    body: compressed,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`Upload failed: ${putRes.status} ${text}`);
  }

  const finalizeBody: FinalizeAvatarRequest = {
    storagePath: signed.storagePath,
    alsoUseOnTree: opts.alsoUseOnTree ?? false,
  };

  return apiFetch<MeResponse>("/me/avatar/finalize", {
    method: "POST",
    body: JSON.stringify(finalizeBody),
  });
}

export async function clearAvatar(): Promise<void> {
  await apiFetch("/me/avatar", { method: "DELETE" });
}
